import React, { useCallback, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { BuySourcesRail } from "@/components/buy-sources";
import { buySourcesPanelState } from "@/lib/buySources/parseSearchResponse";
import { Panel } from "@/components/common/primitives";
import { cn } from "@/lib/utils";

/**
 * Product Analyzer buy-sources panel — explicit Find Sources only (no render-time search).
 */
export function BuySourcesPanel({
  result = null,
  loading = false,
  searched = false,
  zipCode,
  onZipChange,
  onFindSources,
  className,
}) {
  const [localZip, setLocalZip] = useState(zipCode || "");
  const panelState = result ? buySourcesPanelState(result) : null;
  const walmartTarget = result?.walmart_target_price ?? null;

  const handleFind = useCallback(() => {
    onFindSources?.(localZip.trim());
  }, [localZip, onFindSources]);

  return (
    <Panel className={cn("p-3 min-w-0", className)} testid="buy-sources-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="text-stitle text-ink-primary">Buy Sources</div>
        <div className="flex items-center gap-2">
          <input
            value={localZip}
            onChange={(e) => {
              setLocalZip(e.target.value);
              onZipChange?.(e.target.value);
            }}
            placeholder="ZIP"
            className="h-8 w-[84px] px-2 rounded-ctrl border border-edge-subtle text-compact tabular-nums"
            maxLength={10}
            data-testid="buy-sources-zip"
          />
          <button
            type="button"
            onClick={handleFind}
            disabled={loading}
            className="h-8 px-2.5 rounded-ctrl bg-navy-900 text-white text-[12px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="buy-sources-find"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
            Find Sources
          </button>
        </div>
      </div>

      {loading ? (
        <div
          className="h-[58px] rounded-ctrl border border-dashed border-edge-subtle bg-surface-1 animate-pulse"
          data-testid="buy-sources-loading"
        />
      ) : !searched ? (
        <p className="text-[12px] text-ink-muted m-0 py-2" data-testid="buy-sources-idle">
          No buy sources yet. Use Find Sources to search supplier matches for this listing.
        </p>
      ) : panelState === "cheaper" ? (
        <div className="space-y-2" data-testid="buy-sources-state-cheaper">
          <div className="text-[11px] font-semibold text-ink-primary">Cheaper Sources</div>
          <BuySourcesRail
            sources={result.cheaper_sources}
            testId="analyzer-cheaper-sources"
            walmartTargetPrice={walmartTarget}
            showEconomics
          />
          {result.other_sources?.length ? (
            <>
              <div className="text-[11px] font-medium text-ink-secondary pt-1">Other comparable sources</div>
              <BuySourcesRail
                sources={result.other_sources}
                testId="analyzer-other-sources"
                walmartTargetPrice={walmartTarget}
                showEconomics
              />
            </>
          ) : null}
        </div>
      ) : panelState === "comparable_only" ? (
        <div className="space-y-2" data-testid="buy-sources-state-comparable">
          <p className="text-[12px] text-ink-secondary m-0">
            Walmart is currently the lowest verified price.
          </p>
          <div className="text-[11px] font-semibold text-ink-primary">Comparable Sources</div>
          <BuySourcesRail
            sources={result.other_sources}
            testId="analyzer-comparable-sources"
            walmartTargetPrice={walmartTarget}
            showEconomics
          />
        </div>
      ) : panelState === "unavailable" ? (
        <p className="text-[12px] text-ink-muted m-0 py-2" data-testid="buy-sources-state-unavailable">
          Source search unavailable
        </p>
      ) : panelState === "unresolved_only" ? (
        <p className="text-[12px] text-ink-muted m-0 py-2" data-testid="buy-sources-state-unresolved">
          Sources found, but no usable price was available.
        </p>
      ) : (
        <p className="text-[12px] text-ink-muted m-0 py-2" data-testid="buy-sources-state-empty">
          No comparable sources were verified.
        </p>
      )}
    </Panel>
  );
}
